import { getWebOutbox } from "@client/iphone/lib/videoSync/createVideoSync";
import {
  createProgressSync,
  type ProgressFlushResult,
  type ProgressPayload,
} from "@client/iphone/lib/videoSync/progressSync";
import { type StorageLike, type SyncFeature } from "@client/sync";
import { type ProgressRow } from "@lib/videoStateTypes";

export type { ProgressFlushResult, ProgressPayload };

/**
 * The funder-workspace player's watch-progress writes go through this durable
 * outbox (queue + retrying worker), NOT a fire-and-forget mutation: progress
 * accrued offline must survive until reconnect — and, on the iPad native app,
 * survive a force-quit — exactly like the queue player's own progress path.
 *
 * Storage mirrors createVideoSync's split: native persists to localStorage so
 * the queue outlives an app restart; web uses the module-level in-memory
 * per-user outbox (retries within the page session, never persists across a
 * reload, never flushes under another user). The storage KEY is per-user on
 * native too, so a queue written by one signed-in user can never flush under
 * the next user of a shared device — a stale user's entries just sit inert.
 *
 * The key is also distinct from the mobile store's progress queue
 * ("mobileVideoSync.progress.v1"): two workers over one queue double-send.
 */
export function createVideoStatesProgressSync(args: {
  userId: string;
  isNative: boolean;
  onSuccess: (result: ProgressFlushResult, payload: ProgressPayload) => void;
  // Commit gate — the provider passes "not impersonating" so an in-flight
  // send that raced an identity flip is never treated as delivered.
  shouldCommit?: () => boolean;
  // Test seams; production uses the real defaults.
  storage?: StorageLike;
  isOnline?: () => boolean;
}): SyncFeature<ProgressPayload> {
  return createProgressSync({
    onSuccess: args.onSuccess,
    storageKey: `videoStatesSync.progress.v1.${args.userId}`,
    storage:
      args.storage ?? (args.isNative ? undefined : getWebOutbox(args.userId)),
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}

/**
 * Pure LWW reconciliation of one flushed progress result into the
 * getUserVideoState cache value. Extracted from the provider so the ordering
 * rules stay unit-testable:
 *  - `applied: false` (impersonation no-op, or the server's LWW rejected a
 *    stale write) overwrites the cached row with the server's authoritative
 *    `current` — the optimistic row must not outlive its rejection;
 *  - otherwise keep whichever side has the larger clientUpdatedAt, so an
 *    out-of-order flush response can never regress a newer optimistic row.
 */
export function applyProgressFlushResult<T extends { progress: ProgressRow[] }>(
  prev: T | undefined,
  result: ProgressFlushResult,
): T | undefined {
  if (prev == null) {
    return prev;
  }

  const idx = prev.progress.findIndex(
    (r) => r.videoId === result.current.videoId,
  );

  if (!result.applied) {
    const nextProgress =
      idx === -1
        ? [...prev.progress, result.current]
        : prev.progress.map((row, i) => (i === idx ? result.current : row));

    return { ...prev, progress: nextProgress };
  }

  if (idx === -1) {
    return { ...prev, progress: [...prev.progress, result.current] };
  }

  const cached = prev.progress[idx];
  const cachedTs = new Date(cached.clientUpdatedAt).getTime();
  const incomingTs = new Date(result.current.clientUpdatedAt).getTime();

  if (incomingTs < cachedTs) {
    return prev;
  }

  const nextProgress = prev.progress.map((row, i) =>
    i === idx ? result.current : row,
  );

  return { ...prev, progress: nextProgress };
}

/**
 * True when an `applied: false` flush result is just the server no-op'ing a
 * DUPLICATE of a write it already holds — the commit guard deliberately
 * leaves a sent head queued when its worker stopped mid-send, so the
 * successor re-sends it and the strict-LWW same-timestamp check rejects it
 * with `current` equal to what we sent. That rejection carries no new
 * information and must NOT trigger the cross-round invalidate (a refetch
 * storm that can clobber other videos' optimistic rows). A genuine
 * cross-device rejection returns a DIFFERENT row (newer clientUpdatedAt).
 * Timestamps compare by epoch value, not string identity — the server's
 * serialization of the stored instant need not byte-match our ISO input.
 */
export function isDuplicateNoOpRejection(
  payload: ProgressPayload,
  result: ProgressFlushResult,
): boolean {
  return (
    !result.applied &&
    result.current.videoId === payload.videoId &&
    // The playhead must match too: a same-millisecond write from another
    // device with a DIFFERENT position is a genuine conflict, not our echo.
    // A true duplicate echoes our stored value exactly (doubles round-trip
    // losslessly through JSON), so strict equality is safe here.
    result.current.watchedSeconds === payload.watchedSeconds &&
    new Date(result.current.clientUpdatedAt).getTime() ===
      new Date(payload.clientUpdatedAt).getTime()
  );
}
